/**
 * Timeline Editor Component (canvas-v2)
 *
 * Main timeline component assembling ruler, playhead, and tracks.
 * Provides horizontal scrollable timeline view.
 *
 * V2 additions:
 * - Computes audioTrackCount from max trackIndex of audio clips
 * - Toolbar with type-aware Split/Delete/Copy/Paste/Undo/Redo buttons
 */

import { useRef, useCallback, useEffect, useState, useMemo } from 'react'
import { TimelineRuler } from './TimelineRuler'
import { TimelinePlayhead } from './TimelinePlayhead'
import { TimelineTrack } from './TimelineTrack'
import type { TimelineState } from './types'

interface TimelineEditorProps {
  /** Timeline state (controlled by parent) */
  timelineState: TimelineState

  /** Select clip callback */
  onSelectClip: (clipId: string | null, clipType?: 'video' | 'audio' | null) => void

  /** Set playhead callback */
  onSetPlayhead: (position: number) => void

  /** Set zoom callback */
  onSetZoom: (zoomLevel: number) => void

  /** Reorder clip callback */
  onReorderClip: (clipId: string, newOrder: number) => void

  /** Trim clip callback */
  onTrimClip: (clipId: string, trimStart: number, trimEnd: number) => void

  /** Live trim preview callback (no history push) */
  onTrimClipPreview?: (clipId: string, trimStart: number, trimEnd: number) => void

  /** Audio offset change callback */
  onAudioOffsetChange?: (clipId: string, startOffset: number) => void

  /** Audio track index change callback */
  onAudioTrackIndexChange?: (clipId: string, newTrackIndex: number) => void

  /** Ref tracking playhead position at 60fps during playback */
  playheadPositionRef?: React.RefObject<number>

  /** Whether timeline is read-only */
  readOnly?: boolean

  /** Callback to split the selected clip at playhead */
  onSplitSelectedClip?: () => void

  /** Callback to delete the selected clip */
  onDeleteSelectedClip?: () => void

  /** Callback to copy the selected clip */
  onCopyClip?: () => void

  /** Callback to paste the copied clip */
  onPasteClip?: () => void

  /** Callback to undo */
  onUndo?: () => void

  /** Callback to redo */
  onRedo?: () => void

  /** Whether undo is available */
  canUndo?: boolean

  /** Whether redo is available */
  canRedo?: boolean
}

const RULER_HEIGHT = 30
const VIDEO_TRACK_HEIGHT = 100
const AUDIO_TRACK_HEIGHT = 60
const MIN_TIMELINE_WIDTH = 1200

export function TimelineEditor({
  timelineState,
  onSelectClip,
  onSetPlayhead,
  onSetZoom,
  onReorderClip,
  onTrimClip,
  onTrimClipPreview,
  onAudioOffsetChange,
  onAudioTrackIndexChange,
  playheadPositionRef,
  readOnly = false,
  onSplitSelectedClip,
  onDeleteSelectedClip,
  onCopyClip,
  onPasteClip,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
}: TimelineEditorProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [snapPosition, setSnapPosition] = useState<number | null>(null)

  // Compute audio track count from max trackIndex
  const audioTrackCount = useMemo(() => {
    if (timelineState.audioClips.length === 0) return 1
    const maxTrackIndex = Math.max(...timelineState.audioClips.map((c) => c.trackIndex))
    return maxTrackIndex + 1
  }, [timelineState.audioClips])

  // Handle timeline click (move playhead)
  const handleTimelineClick = useCallback(
    (e: React.MouseEvent) => {
      if (readOnly || timelineState.isPlaying) return

      const container = scrollContainerRef.current
      if (!container) return

      const rect = container.getBoundingClientRect()
      const relativeX = e.clientX - rect.left + container.scrollLeft
      const time = relativeX / timelineState.zoomLevel

      onSetPlayhead(Math.max(0, Math.min(time, timelineState.totalDuration)))
    },
    [readOnly, timelineState.isPlaying, timelineState.zoomLevel, timelineState.totalDuration, onSetPlayhead]
  )

  // Handle zoom
  const handleZoom = useCallback(
    (delta: number) => {
      const newZoom = Math.max(10, Math.min(200, timelineState.zoomLevel + delta))
      onSetZoom(newZoom)
    },
    [timelineState.zoomLevel, onSetZoom]
  )

  // Scroll playhead into view if needed
  useEffect(() => {
    if (timelineState.isPlaying && scrollContainerRef.current) {
      const playheadPixelPos = timelineState.playheadPosition * timelineState.zoomLevel
      const container = scrollContainerRef.current
      const containerWidth = container.clientWidth
      const scrollLeft = container.scrollLeft

      // Auto-scroll if playhead is near edge
      if (playheadPixelPos > scrollLeft + containerWidth - 100) {
        container.scrollLeft = playheadPixelPos - containerWidth / 2
      }
    }
  }, [timelineState.playheadPosition, timelineState.zoomLevel, timelineState.isPlaying])

  const timelineWidth = Math.max(
    timelineState.totalDuration * timelineState.zoomLevel,
    MIN_TIMELINE_WIDTH
  )

  const totalHeight = RULER_HEIGHT + VIDEO_TRACK_HEIGHT + AUDIO_TRACK_HEIGHT * audioTrackCount

  // Type-aware toolbar helpers
  const hasSelection = timelineState.selectedClipId !== null
  const hasCopied = timelineState.copiedClip !== null

  return (
    <div className="flex flex-col h-full bg-card">
      {/* Toolbar: Zoom controls + editing actions */}
      <div className="flex items-center gap-2 px-4 py-2 bg-card-hover border-b border-border">
        {/* Zoom controls */}
        <span className="text-text-secondary text-sm">Zoom:</span>
        <input
          type="range"
          min="10"
          max="200"
          value={timelineState.zoomLevel}
          onChange={(e) => onSetZoom(Number(e.target.value))}
          className="w-24"
          disabled={readOnly}
        />
        <span className="text-text-secondary text-sm min-w-[3ch]">
          {Math.round((timelineState.zoomLevel / 50) * 100)}%
        </span>
        <button
          onClick={() => handleZoom(10)}
          className="px-2 py-1 bg-card text-foreground text-sm rounded hover:bg-canvas"
          disabled={readOnly}
        >
          +
        </button>
        <button
          onClick={() => handleZoom(-10)}
          className="px-2 py-1 bg-card text-foreground text-sm rounded hover:bg-canvas"
          disabled={readOnly}
        >
          -
        </button>

        {/* Separator */}
        <div className="w-px h-5 bg-border mx-1" />

        {/* Editing toolbar actions */}
        {!readOnly && (
          <>
            <ToolbarButton
              label="Split"
              icon={<SplitIcon />}
              onClick={onSplitSelectedClip}
              disabled={!hasSelection}
              title={hasSelection
                ? `Split ${timelineState.selectedClipType || ''} clip at playhead`
                : 'Select a clip to split'
              }
            />
            <ToolbarButton
              label="Delete"
              icon={<DeleteIcon />}
              onClick={onDeleteSelectedClip}
              disabled={!hasSelection}
              title={hasSelection
                ? `Delete selected ${timelineState.selectedClipType || ''} clip`
                : 'Select a clip to delete'
              }
            />
            <ToolbarButton
              label="Copy"
              icon={<CopyIcon />}
              onClick={onCopyClip}
              disabled={!hasSelection}
              title="Copy selected clip"
            />
            <ToolbarButton
              label="Paste"
              icon={<PasteIcon />}
              onClick={onPasteClip}
              disabled={!hasCopied}
              title={hasCopied
                ? `Paste ${timelineState.copiedClipType || ''} clip`
                : 'Copy a clip first'
              }
            />

            {/* Separator */}
            <div className="w-px h-5 bg-border mx-1" />

            <ToolbarButton
              label="Undo"
              icon={<UndoIcon />}
              onClick={onUndo}
              disabled={!canUndo}
              title="Undo (Ctrl+Z)"
            />
            <ToolbarButton
              label="Redo"
              icon={<RedoIcon />}
              onClick={onRedo}
              disabled={!canRedo}
              title="Redo (Ctrl+Shift+Z)"
            />
          </>
        )}

        {/* Playhead time display */}
        <div className="ml-auto text-foreground text-sm font-mono">
          {formatTime(timelineState.playheadPosition)} / {formatTime(timelineState.totalDuration)}
        </div>
      </div>

      {/* Scrollable timeline area */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-auto"
      >
        <div
          className="relative"
          style={{ width: `${timelineWidth}px`, height: `${totalHeight}px` }}
          onClick={handleTimelineClick}
        >
          {/* Ruler */}
          <div className="sticky top-0 z-20">
            <TimelineRuler
              totalDuration={timelineState.totalDuration}
              zoomLevel={timelineState.zoomLevel}
              viewportWidth={MIN_TIMELINE_WIDTH}
              height={RULER_HEIGHT}
            />
          </div>

          {/* Video track */}
          <TimelineTrack
            type="video"
            label="Video"
            clips={timelineState.clips}
            zoomLevel={timelineState.zoomLevel}
            totalDuration={timelineState.totalDuration}
            selectedClipId={timelineState.selectedClipId}
            onSelectClip={(id) => onSelectClip(id, 'video')}
            onReorderClip={onReorderClip}
            onTrimClip={onTrimClip}
            onTrimClipPreview={onTrimClipPreview}
            onSnapPositionChange={setSnapPosition}
            height={VIDEO_TRACK_HEIGHT}
            locked={readOnly}
            minWidth={MIN_TIMELINE_WIDTH}
          />

          {/* Audio tracks */}
          <TimelineTrack
            type="audio"
            label="Audio"
            clips={timelineState.audioClips}
            zoomLevel={timelineState.zoomLevel}
            totalDuration={timelineState.totalDuration}
            selectedClipId={timelineState.selectedClipId}
            onSelectClip={(id) => onSelectClip(id, 'audio')}
            onTrimClip={onTrimClip}
            onAudioOffsetChange={onAudioOffsetChange}
            onAudioTrackIndexChange={onAudioTrackIndexChange}
            audioTrackCount={audioTrackCount}
            height={AUDIO_TRACK_HEIGHT}
            locked={readOnly}
            minWidth={MIN_TIMELINE_WIDTH}
          />

          {/* Playhead (overlays all tracks) */}
          <TimelinePlayhead
            position={timelineState.playheadPosition}
            positionRef={playheadPositionRef}
            totalDuration={timelineState.totalDuration}
            zoomLevel={timelineState.zoomLevel}
            height={totalHeight}
            onPositionChange={onSetPlayhead}
            isPlaying={timelineState.isPlaying}
          />

          {/* Snap indicator (yellow line during drag) */}
          {snapPosition !== null && (
            <div
              className="absolute top-0 w-0.5 bg-yellow-400 pointer-events-none z-30 animate-pulse"
              style={{
                left: `${snapPosition}px`,
                height: `${totalHeight}px`,
                boxShadow: '0 0 8px rgba(250, 204, 21, 0.6)',
              }}
            />
          )}
        </div>
      </div>

      {/* Empty state */}
      {timelineState.clips.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <div className="text-text-muted text-lg mb-2">No clips on timeline</div>
            <div className="text-foreground/30 text-sm">
              Connect video nodes to the VideoEditor node to add clips
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ---- Toolbar button ---- */

interface ToolbarButtonProps {
  label: string
  icon: React.ReactNode
  onClick?: () => void
  disabled?: boolean
  title?: string
}

function ToolbarButton({ label, icon, onClick, disabled, title }: ToolbarButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`
        flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors
        ${disabled
          ? 'text-text-muted cursor-not-allowed'
          : 'text-text-secondary hover:bg-canvas hover:text-foreground'
        }
      `}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  )
}

/* ---- Icons (inline SVG) ---- */

function SplitIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m-4-4l4 4 4-4" />
    </svg>
  )
}

function DeleteIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  )
}

function CopyIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  )
}

function PasteIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  )
}

function UndoIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v2M3 10l4 4M3 10l4-4" />
    </svg>
  )
}

function RedoIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a5 5 0 00-5 5v2M21 10l-4 4M21 10l-4-4" />
    </svg>
  )
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}
